import React from "react";
import moment from "moment";
import { AndroidOutlined, AppleOutlined, ChromeOutlined } from "@ant-design/icons";

export const getInitials = ({ name, surname }) => `${ name?.[ 0 ] || "" }${ surname?.[ 0 ] || "" }`;

export const hasScanLocation = (log) => (
    log?.latitude !== null &&
    log?.longitude !== null &&
    log?.latitude !== undefined &&
    log?.longitude !== undefined &&
    Number.isFinite(Number(log.latitude)) &&
    Number.isFinite(Number(log.longitude))
);

export const getScanPoints = (logs = []) => logs.filter(hasScanLocation);

export const getLocationLatitude = (location) => location?.latitude ?? location?.lat;
export const getLocationLongitude = (location) => location?.longitude ?? location?.lng;

export const hasLocationCoordinates = (location) => hasScanLocation({
    latitude: getLocationLatitude(location),
    longitude: getLocationLongitude(location),
});

export const formatWorkedSeconds = (value) => {
    const seconds = Number(value);

    if (!Number.isFinite(seconds))
    {
        return "-";
    }

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    return `${ String(hours).padStart(2, "0") }:${ String(minutes).padStart(2, "0") }`;
};

export const getWorkDate = (value) => moment(value).format("YYYY-MM-DD");

const getWorkedSeconds = (shift) => {
    if (!shift.check_in_at || !shift.check_out_at)
    {
        return 0;
    }

    return Math.max(0, moment(shift.check_out_at).diff(moment(shift.check_in_at), "seconds"));
};

const sortByCreatedAt = (items = []) => (
    [ ...items ].sort((firstItem, secondItem) => (
        moment(firstItem.created_at).valueOf() - moment(secondItem.created_at).valueOf()
    ))
);

export const attachScanLogsToShifts = (shifts = [], scanLogs = []) => {
    const scanLogsByShiftId = scanLogs.reduce((logsByShift, log) => {
        if (!log.attendance_shift_id)
        {
            return logsByShift;
        }

        return {
            ...logsByShift,
            [ log.attendance_shift_id ]: [
                ...(logsByShift[ log.attendance_shift_id ] || []),
                log,
            ],
        };
    }, {});

    return shifts.map((shift) => ({
        ...shift,
        work_date: getWorkDate(shift.check_in_at),
        scan_logs: sortByCreatedAt(scanLogsByShiftId[ shift.id ]),
    }));
};

export const buildAttendanceDailySummaries = (shifts = []) => (
    Object.values(shifts.reduce((summaries, shift) => {
        const workDate = getWorkDate(shift.check_in_at);
        const key = `${ shift.ee_id }-${ shift.user_id }-${ workDate }`;
        const currentSummary = summaries[ key ];
        const checkInAt = !currentSummary || moment(shift.check_in_at).isBefore(currentSummary.check_in_at) ?
            shift.check_in_at :
            currentSummary.check_in_at;
        const checkOutAt = currentSummary?.has_open_shift || !shift.check_out_at ?
            null :
            (!currentSummary?.check_out_at || moment(shift.check_out_at).isAfter(currentSummary.check_out_at) ?
                shift.check_out_at :
                currentSummary.check_out_at
            );

        return {
            ...summaries,
            [ key ]: {
                id: key,
                user_id: shift.user_id,
                ee_id: shift.ee_id,
                worker: shift.worker,
                subcontractor: shift.subcontractor,
                work_date: workDate,
                check_in_at: checkInAt,
                check_out_at: checkOutAt,
                worked_seconds: (currentSummary?.worked_seconds || 0) + getWorkedSeconds(shift),
                has_open_shift: Boolean(currentSummary?.has_open_shift || !shift.check_out_at),
                scan_logs: sortByCreatedAt([
                    ...(currentSummary?.scan_logs || []),
                    ...(shift.scan_logs || []),
                ]),
            },
        };
    }, {})).sort((firstSummary, secondSummary) => (
        moment(secondSummary.work_date).valueOf() - moment(firstSummary.work_date).valueOf()
    ))
);

const scanStatusMap = {
    success: { color: "green", label: "CHECK_LOGS.Status_Success" },
    warning: { color: "orange", label: "CHECK_LOGS.Status_Warning" },
    rejected: { color: "red", label: "CHECK_LOGS.Status_Rejected" },
};

const scanReasonMap = {
    within_radius: "CHECK_LOGS.Reason_WithinRadius",
    outside_radius: "CHECK_LOGS.Reason_OutsideRadius",
    fake_gps_suspected: "CHECK_LOGS.Reason_FakeGpsSuspected",
    gps_unavailable: "CHECK_LOGS.Reason_GpsUnavailable",
    permission_denied: "CHECK_LOGS.Reason_PermissionDenied",
    qr_invalid: "CHECK_LOGS.Reason_QrInvalid",
    duplicate_scan: "CHECK_LOGS.Reason_DuplicateScan",
};

const platformMap = {
    android: { icon: <AndroidOutlined />, label: "Android" },
    ios: { icon: <AppleOutlined />, label: "iOS" },
    web: { icon: <ChromeOutlined />, label: "Web" },
};

export const getScanStatusMeta = (status) => scanStatusMap[ status ] || scanStatusMap.rejected;
export const getScanReasonLabel = (reason) => scanReasonMap[ reason ];
export const getPlatformMeta = (platform) => platformMap[ platform ] || platformMap.web;
